import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { adminAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, PlusCircle, Pencil } from 'lucide-react';

const emptyForm = {
  title: '', slug: '', excerpt: '', content: '',
  author: 'Admin', metaTitle: '', metaDescription: '', coverImage: null
};

const CreateBlogModal = ({ onSuccess, editData = null }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const isEdit = !!editData;

  useEffect(() => {
    if (open && editData) {
      setFormData({
        title: editData.title || '',
        slug: editData.slug || '',
        excerpt: editData.excerpt || '',
        content: editData.content || '',
        author: editData.author || 'Admin',
        metaTitle: editData.metaTitle || '',
        metaDescription: editData.metaDescription || '',
        coverImage: null
      });
    } else if (open) {
      setFormData(emptyForm);
    }
  }, [open, editData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'title' && !isEdit && !formData.slug) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, [name]: value, slug }));
    }
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, coverImage: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug || !formData.content) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await adminAPI.blog.update(editData.id, {
          title: formData.title,
          slug: formData.slug,
          excerpt: formData.excerpt,
          content: formData.content,
          author: formData.author,
          metaTitle: formData.metaTitle || formData.title,
          metaDescription: formData.metaDescription || formData.excerpt
        });
        toast({ title: 'Updated', description: 'Blog post updated successfully' });
      } else {
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('slug', formData.slug);
        submitData.append('excerpt', formData.excerpt);
        submitData.append('content', formData.content);
        submitData.append('author', formData.author);
        submitData.append('metaTitle', formData.metaTitle || formData.title);
        submitData.append('metaDescription', formData.metaDescription || formData.excerpt);
        if (formData.coverImage) submitData.append('coverImage', formData.coverImage);
        await adminAPI.blog.create(submitData);
        toast({ title: 'Created', description: 'Blog post created successfully' });
      }
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Operation failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="ghost" data-testid={`edit-blog-${editData.id}`} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
            <Pencil className="w-4 h-4" />
          </Button>
        ) : (
          <Button className="bg-purple-600 hover:bg-purple-700" data-testid="add-blog-btn">
            <PlusCircle className="w-4 h-4 mr-2" /> Create Post
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="blog-title">Title *</Label>
            <Input id="blog-title" name="title" value={formData.title} onChange={handleInputChange} required data-testid="blog-title-input" />
          </div>
          <div>
            <Label htmlFor="blog-slug">Slug *</Label>
            <Input id="blog-slug" name="slug" value={formData.slug} onChange={handleInputChange} placeholder="my-blog-post-title" required data-testid="blog-slug-input" />
            <p className="text-sm text-gray-500 mt-1">URL-friendly version of the title</p>
          </div>
          <div>
            <Label htmlFor="blog-excerpt">Excerpt</Label>
            <Textarea id="blog-excerpt" name="excerpt" value={formData.excerpt} onChange={handleInputChange} rows={2} placeholder="Brief summary of the post..." />
          </div>
          <div>
            <Label htmlFor="blog-content">Content *</Label>
            <Textarea id="blog-content" name="content" value={formData.content} onChange={handleInputChange} rows={10}
              placeholder="Write your blog post content here. You can use HTML tags for formatting." required data-testid="blog-content-input" />
            <p className="text-sm text-gray-500 mt-1">Supports HTML formatting</p>
          </div>
          <div>
            <Label htmlFor="blog-author">Author</Label>
            <Input id="blog-author" name="author" value={formData.author} onChange={handleInputChange} />
          </div>
          {!isEdit && (
            <div>
              <Label htmlFor="blog-image">Cover Image</Label>
              <Input id="blog-image" name="coverImage" type="file" onChange={handleFileChange} accept="image/*" />
            </div>
          )}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3">SEO Settings (Optional)</h3>
            <div className="space-y-3">
              <div>
                <Label htmlFor="blog-meta-title">Meta Title</Label>
                <Input id="blog-meta-title" name="metaTitle" value={formData.metaTitle} onChange={handleInputChange} placeholder="Leave empty to use post title" />
              </div>
              <div>
                <Label htmlFor="blog-meta-desc">Meta Description</Label>
                <Textarea id="blog-meta-desc" name="metaDescription" value={formData.metaDescription} onChange={handleInputChange} rows={2} placeholder="Leave empty to use excerpt" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="blog-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isEdit ? 'Saving...' : 'Publishing...'}</> : (isEdit ? 'Save Changes' : 'Publish Post')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateBlogModal;
